import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Download, 
  Shield, 
  Lock,
  Building2,
  AlertTriangle,
  CheckCircle,
  Copy,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The service agreement content
const SERVICE_AGREEMENT = `
# MASTER SERVICE AGREEMENT

## TECHNOLOGY SERVICES AND INTELLECTUAL PROPERTY LICENSE AGREEMENT

**Effective Date:** _________________, 20___

---

## PARTIES

**SERVICE PROVIDER:**
Very Big Box LLC ("VBB" or "Provider")

**CLIENT:**
_________________________________ ("Club" or "Client")

---

## SUMMARY OF KEY TERMS

| Term | Value |
|------|-------|
| **Standard Service Fee** | 45% of Gross Revenues |
| **AADC Founding Club Fee** | 75% until 10,000 paid members, then 45% |
| **Permanent IP Royalty** | 3% of Gross Revenues (perpetual, non-negotiable) |
| **Renegotiation Threshold** | 100,000 paid members |
| **Initial Term** | 5 years (auto-renew 5-year terms) |
| **Non-Renewal Notice** | 12 months before term end |
| **Termination for Convenience** | Not permitted |
| **Buyout Option** | 12-36 months fees (varies by timing) |
| **Right of First Refusal** | VBB can match competitor offers |
| **Uptime SLA** | 99.5% |

---

## ARTICLE 1: DEFINITIONS

**1.1 "Gross Revenues"** means all revenues received by the Club from any and all sources, including but not limited to: membership dues and fees, event registration fees, merchandise sales, sponsorships, donations, grants, advertising revenue, partnership fees, and any other income.

**1.2 "Platform"** means the technology system developed and maintained by VBB, including all software, databases, user interfaces, APIs, hosting infrastructure, and related components.

---

## ARTICLE 2: SERVICES

### 2.1 Services Provided by VBB

- **Platform Hosting**: Secure, professionally managed servers with 99.5% uptime
- **Technical Infrastructure**: DNS management, SSL/TLS certificates, CDN services
- **Software Maintenance**: Updates, patches, security fixes, feature enhancements
- **Technical Support**: Tier 2/3 support, 4-hour critical response time
- **Data Management**: Database administration, backups, data export capabilities
- **Payment Processing**: Credit card, PayPal, and other payment gateway management

### 2.2 Services Retained by the Club

- Day-to-day management and governance
- First-tier member support and customer service
- Content creation and moderation
- Marketing and promotional activities
- Financial management and accounting

---

## ARTICLE 3: INTELLECTUAL PROPERTY

### 3.1 Platform License
VBB grants Club a **non-exclusive, non-transferable, revocable license** to use the Platform and all intellectual property developed or owned by Very Big Box LLC, including but not limited to:
- Software, source code, and compiled applications
- User interfaces, designs, and visual elements
- APIs, integrations, and technical documentation
- Databases, data structures, and algorithms
- Trademarks, trade dress, and branding elements (as authorized)
- Patents, trade secrets, and proprietary methodologies

### 3.2 Scope of License
The license granted herein permits Club to:
- Access and use the Platform for Club operations
- Display VBB-owned content and features to Club members
- Integrate with VBB-provided APIs and services
- Use documentation and training materials

### 3.3 Restrictions
Club shall not:
- Sublicense, sell, or transfer Platform access to third parties
- Reverse engineer, decompile, or derive source code
- Remove or alter proprietary notices or branding
- Use the Platform for purposes outside Club operations
- Create derivative works without written consent

### 3.4 Ownership
VBB retains all ownership of intellectual property. This Agreement does not transfer any ownership rights to Club. Club retains ownership of its own data, content, and member information.

### 3.5 Termination of License
Upon termination of this Agreement, all IP licenses granted herein shall immediately terminate. Club shall cease use of all VBB intellectual property within 30 days.

---

## ARTICLE 4: COMPENSATION

### 4.1 Total Service Fee
**Forty-five percent (45%)** of Club's Gross Revenues, allocated as follows:

### 4.2 Fee Breakdown

| Category | Percentage | Description |
|----------|------------|-------------|
| **Engineering & Development** | 25% | Platform development, feature enhancements, bug fixes, security updates, R&D for new capabilities, code maintenance, quality assurance |
| **Infrastructure & Operations** | 12% | Server hosting, database management, CDN services, DNS management, SSL/TLS certificates, bandwidth, storage, backups, monitoring, DevOps |
| **Customer Support** | 5% | Tier 2/3 technical support, documentation, training materials, onboarding assistance, escalation handling |
| **Payment Processing** | 3% | Credit card fees, PayPal fees, Stripe fees, ACH processing, chargeback handling, PCI compliance |
| **Total** | **45%** | |

### 4.3 Founding Club Early-Stage Fee (AADC Special Provision)

**This section applies exclusively to All American Dog Club as the founding/first club on the Platform.**

#### 4.3.1 VBB Full-Service Contributions

During the early-stage period, VBB provides comprehensive services beyond the standard platform offering:

**Intellectual Property & Concept:**
- Original conception and design of the club platform model
- Business model architecture and multi-club framework
- Brand development and positioning strategy
- All proprietary systems, workflows, and methodologies

**Capital Investment:**
- Initial member acquisition campaigns (target: $50,000 promotional investment)
- Platform development and infrastructure buildout
- Ongoing operational funding until self-sustaining

**Operations & Management:**
- Day-to-day club operations and administration
- First-tier member support and customer service
- Content moderation and community management
- Strategic direction until elected governance is established

**Business Development:**
- Sponsor acquisition and relationship management
- Partnership development and outreach
- Vendor and service provider negotiations
- Revenue diversification initiatives

#### 4.3.2 Fee Structure

| Threshold | Service Fee | Phase |
|-----------|-------------|-------|
| **0 - 9,999 paid members** | **75%** of Gross Revenues | **Full-Service**: VBB provides platform + IP + capital + operations + marketing + management + support |
| **10,000+ paid members** | **45%** of Gross Revenues | **Platform-Only**: Club assumes self-governance; VBB provides platform services per Article 2 |

#### 4.3.3 Terms

- "Paid members" means members with active Full Member or Premium subscriptions (excludes free tier)
- Threshold is measured as of the last day of each calendar month
- Once the 10,000 paid member threshold is reached and maintained for two (2) consecutive months, the standard 45% fee permanently applies
- Transition to 45% rate coincides with Club establishing elected governance (board, officers) and assuming operational responsibilities outlined in Article 2.2
- If paid membership falls below 10,000 after reaching the threshold, the 75% rate does **not** reinstate
- This provision is non-transferable and applies only to AADC; subsequent clubs begin at the standard 45% rate
- VBB retains all intellectual property rights regardless of fee tier (see Article 3)

#### 4.3.4 Value Provided

Club acknowledges that VBB is providing a **complete turnkey business operation** requiring no upfront capital investment from Club, including:

- Proven technology platform (development cost: $500K+ equivalent)
- Intellectual property and proprietary business model
- Operational infrastructure and systems
- Initial member acquisition investment (target: $50,000)
- Ongoing platform development and feature releases
- Technical support and maintenance
- Payment processing and financial systems
- Compliance and security infrastructure

The Service Fee structure reflects this comprehensive value transfer and VBB's assumption of all startup risk. Club receives a fully operational business from day one with no capital outlay, technology development, or infrastructure buildout required.

#### 4.3.5 Renegotiation at Scale

Upon reaching **100,000 paid members**, either party may request renegotiation of:
- Service Fee percentages and allocation
- Division of operational responsibilities
- Support tier assignments
- Additional services and pricing

Such renegotiation shall be conducted in good faith with the goal of establishing terms appropriate for a mature, large-scale organization. Until new terms are mutually agreed upon in writing, existing terms remain in effect.

**Exclusion from Renegotiation:** The Permanent IP Royalty (Section 4.3.6) is not subject to renegotiation and remains in effect in perpetuity.

#### 4.3.6 Permanent IP Royalty

In recognition of VBB's original invention, conception, and development of the club platform model, business architecture, and proprietary systems, Club agrees to pay VBB a **permanent intellectual property royalty** as follows:

| Component | Rate | Duration |
|-----------|------|----------|
| **IP Royalty** | **3%** of Gross Revenues | **Perpetual** (survives termination, renegotiation, or transfer) |

**Terms:**
- This royalty is separate from and in addition to any Service Fees
- The IP Royalty survives termination of this Agreement for any reason
- The IP Royalty survives any renegotiation of other terms under Section 4.3.5
- The IP Royalty transfers with any sale, assignment, or transfer of Club ownership or operations
- The IP Royalty applies regardless of whether Club continues to use VBB's platform services
- Payment terms follow Section 4.6

### 4.4 Included Services
The Service Fee covers:
- **Non-exclusive license** to all VBB intellectual property (see Article 3)
- All technology services described in Article 2.1
- Payment processing fees (credit cards, PayPal, Stripe, ACH)
- Domain name registration and DNS management
- SSL/TLS certificates and security infrastructure
- Standard hosting, bandwidth, and storage
- Platform updates and new feature releases
- Technical support per SLA (Exhibit A)

### 4.5 Additional Fees Not Covered
- Third-party software licenses requested by Club
- Custom development beyond standard platform features
- Premium/dedicated support packages (24/7, dedicated rep)
- Marketing and advertising costs
- Legal, accounting, and professional services
- Club-specific regulatory compliance
- Extraordinary bandwidth/storage usage (>200% of baseline)

### 4.6 Payment Terms
- Monthly calculation and reporting
- Reports due within 15 days of month end
- Payment due within 30 days of month end

### 4.7 Pricing and Revenue Plans

#### 4.7.1 Mutual Agreement Required
Club pricing structures, membership fees, and revenue-generating activities shall be established **by mutual written agreement** between VBB and the Club. Neither party may unilaterally set or modify pricing that materially affects the other party's financial interests.

#### 4.7.2 Minimum Revenue Requirement
To ensure sustainable platform operations, Club agrees to maintain pricing and membership structures that generate sufficient Gross Revenues to cover VBB's operational costs for the Club. Specifically:

- **Free-Only Models Prohibited**: Club may not offer exclusively free membership without VBB's prior written consent
- **Minimum Viable Revenue**: Club pricing must reasonably be expected to generate Gross Revenues sufficient to cover the Service Fee obligations
- **Revenue Shortfall**: If Club's Gross Revenues fall below the cost of providing services for three (3) consecutive months, VBB may:
  - Require Club to implement paid membership tiers or revenue-generating features
  - Negotiate a minimum monthly fee to cover operational costs
  - Reduce service levels proportionally to available revenue
  - Terminate the Agreement with 30 days' notice if no resolution is reached

#### 4.7.3 Pricing Changes
Any material changes to Club pricing or fee structures require:
- 30 days' advance written notice to VBB
- Good faith discussion of impact on platform sustainability
- Written acknowledgment from VBB before implementation

#### 4.7.4 Annual Review
Parties shall conduct an annual review of pricing structures to ensure alignment with operational costs and market conditions.

---

## ARTICLE 5: TERM AND TERMINATION

### 5.1 Term
- **Initial Term**: 5 years from Effective Date
- **Renewal**: Automatic renewal for successive 5-year terms
- **Non-Renewal Notice**: 12 months prior to end of current term

### 5.2 Termination for Cause
Either party may terminate for material breach with:
- Written notice specifying the breach
- 30-day cure period
- If breach is not cured, termination effective upon written notice

### 5.3 No Termination for Convenience
**Termination for convenience is not permitted.** Club may only exit this Agreement through:
- Termination for cause (Section 5.2)
- Non-renewal with proper notice (Section 5.1)
- Buyout (Section 5.4)

### 5.4 Buyout Option
Club may terminate this Agreement prior to the end of any term by paying an **Early Termination Fee** calculated as:

| Timing | Buyout Fee |
|--------|------------|
| Years 1-2 of any term | 36 months of average monthly Service Fees* |
| Years 3-4 of any term | 24 months of average monthly Service Fees* |
| Year 5 of any term | 12 months of average monthly Service Fees* |

*Based on average of prior 12 months (or actual months if less than 12)

- Buyout Fee due in full before termination effective
- IP Royalty obligations survive and continue in perpetuity (Section 4.3.6)

### 5.5 Right of First Refusal (Competitor Match)
If Club receives a bona fide offer from a competing platform provider, VBB shall have the **right to match** such offer:

- Club must provide VBB written notice of the competing offer, including all material terms
- VBB has 30 days to elect to match the competing offer
- If VBB matches, Agreement continues under matched terms
- If VBB declines to match, Club may accept the competing offer subject to:
  - Payment of applicable Buyout Fee (Section 5.4)
  - Continued IP Royalty obligations (Section 4.3.6)
- Club may not accept a competing offer without first providing VBB the opportunity to match

### 5.6 Post-Termination
- Data export provided within 30 days of termination
- Club must cease use of all VBB intellectual property within 30 days
- IP Royalty obligations survive in perpetuity (Section 4.3.6)
- Confidentiality obligations survive for 5 years

---

## ARTICLE 6: LIABILITY AND INSURANCE

### Limitation of Liability
- No indirect, consequential, or punitive damages
- Cap: 12 months of fees paid

### Required Insurance
- Commercial General Liability: $1M/$2M
- Professional Liability (E&O): $1M/$2M
- Cyber Liability: $1M
- Workers' Compensation: As required by law

---

## ARTICLE 7: GENERAL PROVISIONS

- **Confidentiality**: Mutual NDA provisions
- **Independent Contractors**: No employment relationship
- **Dispute Resolution**: Negotiation → Mediation → Arbitration
- **Governing Law**: [State TBD]

---

## ARTICLE 8: OMBUDSMAN SERVICES

### 8.1 Availability
VBB provides ombudsman services upon request to assist with dispute resolution between members, Clubs, and the platform.

### 8.2 Scope
The ombudsman may assist with member grievances, policy disputes, and general conflict mediation. The ombudsman acts as a neutral third party.

### 8.3 Limitation of Liability
**THE OMBUDSMAN SERVICE IS PROVIDED IN GOOD FAITH ON AN "AS IS" BASIS. VBB SHALL NOT BE LIABLE FOR:**
- Errors, omissions, or recommendations made during ombudsman proceedings
- Outcomes or consequences resulting from ombudsman services
- Failure of parties to reach agreement or follow recommendations
- Any indirect, consequential, or incidental damages

### 8.4 Non-Binding Nature
Ombudsman recommendations are advisory and non-binding. The ombudsman does not have authority to make binding decisions, reverse management actions, or award damages.

### 8.5 Independence
The ombudsman operates independently and shall have immunity for good-faith actions taken in the course of dispute resolution.

---

## SERVICE LEVEL AGREEMENT (EXHIBIT A)

| Priority | Description | Response | Resolution |
|----------|-------------|----------|------------|
| Critical | System down | 4 hours | 24 hours |
| High | Major feature unavailable | 8 hours | 48 hours |
| Medium | Minor feature affected | 2 business days | 5 business days |
| Low | General questions | 5 business days | As scheduled |

---

*This is a summary template. The full agreement document should be reviewed by qualified legal counsel before execution.*

**Document Version**: 2.0  
**Last Updated**: April 2026
`;

const ServiceAgreementPage = () => {
  const { user, token } = useAuth();
  const [copied, setCopied] = useState(false);

  // Access control: VBB personnel (operator admins) and club admins only
  const isVBBPersonnel = user?.is_operator_admin;
  const isClubAdmin = user?.is_admin || ['club_officer', 'ngo_officer'].includes(user?.member_class);
  const hasAccess = isVBBPersonnel || isClubAdmin;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This document is only accessible to VBB personnel and Club administrators.
            </p>
            <Button className="mt-4" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SERVICE_AGREEMENT);
      setCopied(true);
      toast.success('Agreement copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const blob = new Blob([SERVICE_AGREEMENT], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'VBB_Club_Service_Agreement.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Agreement downloaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="service-agreement-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Service Agreement Template</h1>
              <p className="text-slate-300">VBB & Club Master Service Agreement</p>
            </div>
          </div>
        </div>
      </div>

      {/* Access Badge */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            {isVBBPersonnel ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full">
                <Building2 className="w-4 h-4" />
                VBB Personnel Access
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                <Shield className="w-4 h-4" />
                Club Admin Access
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2 print:hidden">
              <Printer className="w-4 h-4" />
              Print
            </Button>
            <Button size="sm" onClick={handleDownload} className="gap-2">
              <Download className="w-4 h-4" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Agreement Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-medium">Template Document - Review with Legal Counsel Before Use</span>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="prose prose-slate max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-table:text-sm">
              <ReactMarkdown>{SERVICE_AGREEMENT}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Confidential - For authorized personnel only</p>
          <p>© {new Date().getFullYear()} Very Big Box LLC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ServiceAgreementPage;
